import React, { useState } from "react";

import { getPortal } from "../portal.js";

/* Language picker with two states:
   - open (no language yet, or "Change language" clicked): a two-column
     grid of option cards - flag left, native name with English name
     beneath.
   - collapsed (language chosen): the selected flag + name next to the
     label, with a "Change language" link to reopen the grid.
   Picking an option calls the same handler with the same
   { target: { name, value } } shape the original <Select> produced, so
   the form state handling is unchanged. Inline SVGs (not emoji flags)
   because Windows renders flag emoji as plain letters. */

const textObject = window.textObject;

const flagSvgs = {
  "English": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="30" fill="#012169" />
      <path d="M0,0L60,30M60,0L0,30" stroke="#ffffff" strokeWidth="6" />
      <path d="M0,0L60,30M60,0L0,30" stroke="#C8102E" strokeWidth="2" />
      <path d="M30,0V30M0,15H60" stroke="#ffffff" strokeWidth="10" />
      <path d="M30,0V30M0,15H60" stroke="#C8102E" strokeWidth="6" />
    </svg>
  ),
  "Español": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="30" fill="#AA151B" />
      <rect y="7.5" width="60" height="15" fill="#F1BF00" />
    </svg>
  ),
  "Nederlands": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="10" fill="#AE1C28" />
      <rect y="10" width="60" height="10" fill="#ffffff" />
      <rect y="20" width="60" height="10" fill="#21468B" />
    </svg>
  ),
  "Русский": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="10" fill="#ffffff" />
      <rect y="10" width="60" height="10" fill="#0039A6" />
      <rect y="20" width="60" height="10" fill="#D52B1E" />
    </svg>
  ),
  "Українська": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="15" fill="#005BBB" />
      <rect y="15" width="60" height="15" fill="#FFD500" />
    </svg>
  ),
  "Český Jazyk, Čeština": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="15" fill="#ffffff" />
      <rect y="15" width="60" height="15" fill="#D7141A" />
      <path d="M0,0L30,15L0,30Z" fill="#11457E" />
    </svg>
  ),
  "Polskie": (
    <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <rect width="60" height="15" fill="#ffffff" />
      <rect y="15" width="60" height="15" fill="#DC143C" />
    </svg>
  )
};

/* US flag shown for the English option when the visitor came from the
   .com portal (the Union Jack in flagSvgs is used for .co.uk). The
   underlying language value stays "English" either way - display only. */
const usFlagSvg = (
  <svg viewBox="0 0 60 30" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <rect width="60" height="30" fill="#ffffff" />
    {[0, 2, 4, 6, 8, 10, 12].map(i => (
      <rect key={i} y={(i * 30) / 13} width="60" height={30 / 13} fill="#B22234" />
    ))}
    <rect width="26" height={(30 / 13) * 7} fill="#3C3B6E" />
    {[3, 8, 13, 18, 23].map(x =>
      [2.7, 8.1, 13.5].map(y => (
        <circle key={x + "-" + y} cx={x} cy={y} r="1.1" fill="#ffffff" />
      ))
    )}
  </svg>
);

const englishName = language =>
  (textObject && textObject[language] && textObject[language]["English Name"]) ||
  language;

const isComEnglish = language => language === "English" && getPortal() === "com";

const displayName = language =>
  isComEnglish(language) ? "English (U.S.)" : language;

const Flag = props => (
  <span className="language-flag-box">
    {(isComEnglish(props.language) && usFlagSvg) ||
      flagSvgs[props.language] || (
        <span className="language-flag-fallback">
          {props.language.slice(0, 2)}
        </span>
      )}
  </span>
);

const LanguageFlags = props => {
  const [changing, setChanging] = useState(false);
  const open = !props.value || changing;

  const choose = language => {
    props.handlechange({ target: { name: "language", value: language } });
    setChanging(false);
  };

  if (!open) {
    return (
      <div className="form-group">
        <label>{props.title}</label>
        <div className="language-selected">
          <Flag language={props.value} />
          <span className="language-option-native">
            {displayName(props.value)}
          </span>
          <button
            type="button"
            className="language-change"
            onClick={() => setChanging(true)}
          >
            Change language
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="form-group">
      <label>{props.title}</label>
      <div className="language-options">
        {props.options.map(language => (
          <button
            type="button"
            key={language}
            aria-pressed={props.value === language}
            className={
              "language-option" + (props.value === language ? " selected" : "")
            }
            onClick={() => choose(language)}
          >
            <Flag language={language} />
            <span className="language-option-text">
              <span className="language-option-native">
                {displayName(language)}
              </span>
              <span className="language-option-english">
                {englishName(language)}
              </span>
            </span>
            {props.value === language && (
              <span className="language-option-check" aria-hidden="true">
                ✓
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default LanguageFlags;
